// ─── Enum string unions ───────────────────────────────────────────────────────

export type TriggerSource = "eventbridge" | "sqs" | "dynamodb_stream" | "crm";

export type ResultType = "offer" | "award" | "info";

/** Inform intents write a banner and do not grant value. */
export const isInformResultType = (resultType: string | undefined): boolean =>
  resultType === "offer" || resultType === "info";

/** Campaign category discriminator (BONENG-722/724/842). */
export type CampaignCategory = "standard" | "referral" | "series";

export const DYNAMIC_BONUS_CAMPAIGN_STATUS = {
  DRAFT: "draft",
  ACTIVE: "active",
  PAUSED: "paused",
  EXPIRED: "expired",
  ARCHIVED: "archived",
} as const;

export type DynamicBonusCampaignStatus =
  (typeof DYNAMIC_BONUS_CAMPAIGN_STATUS)[keyof typeof DYNAMIC_BONUS_CAMPAIGN_STATUS];

export type DynamicBonusArchivedStatus =
  typeof DYNAMIC_BONUS_CAMPAIGN_STATUS.ARCHIVED;

export type SegmentStatus = "active" | "paused" | "archived";

export type FieldOperator =
  | "eq"
  | "ne"
  | "gt"
  | "gte"
  | "lt"
  | "lte"
  | "exists"
  | "in";

/** Screens a bonus banner may appear on (BONENG-921). Mirrors the backend DisplayLocation enum. */
export type DisplayLocation = "sportsbook" | "casino" | "discover" | "profile" | "deposit";

// ─── Nested specs ─────────────────────────────────────────────────────────────

export type TriggerSpecIn = {
  // Sole trigger identifier for campaigns/templates (catalog `event_key`). The
  // backend derives `source` server-side and forbids it on write
  // (`extra="forbid"`). Nullable only when reading legacy steps created
  // before the catalog migration.
  event_key: string | null;
  // Present on list/detail reads from bonus-service-v2; omitted on writes.
  event_type?: string | null;
  source?: TriggerSource | null;
  selector?: Record<string, unknown> | null;
};

export type ConditionSpecIn = {
  type: string;
  params?: Record<string, unknown>;
};

export type ResultSpecIn = {
  type: string;
  result_type: ResultType;
  params?: Record<string, unknown>;
};

export type CapSpecIn = {
  max_awards_per_user?: number | null;
};

// ─── Referral config (category=referral) ──────────────────────────────────────

/** Per-product referrer payout amounts. `dual` is the dual-referral claim. */
export type ReferralAmountsIn = {
  sportsbook?: number | null;
  casino?: number | null;
  dual?: number | null;
};

/**
 * Config block for a `category=referral` campaign. Mirrors the bonus-service-v2
 * `ReferralConfigIn` contract (BONENG-722). Expiry and gating reuse the
 * campaign-level `ends_at` / `eligibility_conditions`.
 */
export type ReferralConfigIn = {
  qualifying_deposit_threshold: number;
  amounts?: ReferralAmountsIn;
  eligible_products?: string[];
  enabled?: boolean;
};

// ─── Step banner (structured, per-locale) ─────────────────────────────────────

/** locale -> text, e.g. `{ en: "Claim your bonus" }`. Backend falls back locale -> `en`. */
export type LocalizedTextMap = Record<string, string>;

export type LocalizedTextIn = {
  text?: LocalizedTextMap;
  color?: string | null;
  shadow?: boolean;
};

export type BannerDetailsIn = {
  visible?: boolean;
  text?: LocalizedTextMap;
  color?: string | null;
  shadow?: boolean;
};

export type BannerTimerIn = {
  visible?: boolean;
  background_color?: string | null;
  text_color?: string | null;
  icon_color?: string | null;
  border_color?: string | null;
  /** Border width in pixels. */
  border_width?: number | null;
  shadow?: boolean;
};

export type BannerClaimIn = {
  visible?: boolean;
  text?: LocalizedTextMap;
  text_color?: string | null;
  /** Gradient stops (2+ hex colors); intentionally an array, unlike `border_color` below. */
  background_color?: string[];
  border_color?: string | null;
  /** Border width in pixels. */
  border_width?: number | null;
  shadow?: boolean;
};

export type BannerImageIn = { url?: string | null };

export type BannerDeeplinkScreen =
  | "sportsbook_league"
  | "sportsbook"
  | "sportsbook_match"
  | "casino"
  | "casino_game"
  | "casino_provider"
  | "casino_collection"
  | "casino_category"
  | "deposit"
  | "user_profile"
  | "poker_table"
  | "poker_lobby"
  | "game_pass";

export type BannerDeeplinkIn = {
  screen: BannerDeeplinkScreen;
  tournament_id?: string;
  sport_id?: string;
  event_id?: string;
  money_type?: "cash" | "coin";
  game_id?: string;
  provider_id?: string;
  eid?: string;
  code?: string;
};

/**
 * Structured per-step banner (colors, localized text blocks, timer, claim
 * button, image and click-through destination).
 * Every section is optional — a minimal banner
 * (`{ title: { text: { en: "Hi" } } }`) is valid. Backend schema is
 * `extra="forbid"`, so the structured deeplink requires matching backend
 * support before it can be submitted successfully. `banner.title` /
 * `banner.short_description` win over the legacy campaign-level
 * `localized_strings` chain at runtime; the `banner_title_key`/
 * `banner_msg_key` result params remain only as the legacy fallback.
 */
export type BannerIn = {
  /** Gradient stops (2+ hex colors), like `BannerClaimIn.background_color`. Server omits the key rather than nulling it when unset. */
  background_color?: string[] | null;
  /** Gradient stops, unlike the single-color `BannerClaimIn.border_color`. Server omits the key rather than nulling it when unset. */
  border_color?: string[] | null;
  /** Strings are read-only legacy values; newly built payloads emit `BannerDeeplinkIn`. */
  deeplink?: BannerDeeplinkIn | string | null;
  title?: LocalizedTextIn | null;
  short_description?: LocalizedTextIn | null;
  long_description?: LocalizedTextIn | null;
  details?: BannerDetailsIn | null;
  timer?: BannerTimerIn | null;
  claim?: BannerClaimIn | null;
  image?: BannerImageIn | null;
};

export type StepSpecIn = {
  step_id: string;
  trigger: TriggerSpecIn;
  conditions?: ConditionSpecIn[];
  results: ResultSpecIn[];
  banner?: BannerIn | null;
  /**
   * BONENG-921 — screens this step's bonus shows on. Omitted/null = "not
   * configured" (backend classifies automatically at serve time). Backend is
   * `extra="forbid"`: only ship PAM PRs after backend PR 1 is deployed.
   */
  display_locations?: DisplayLocation[] | null;
  /**
   * BONENG-813 — freeform image/button placement for standalone display
   * banners. Omitted for bonus result types.
   */
  freeform_layout?: {
    image: { x: number; y: number; width: number };
    button: { x: number; y: number; width: number; height: number };
  } | null;
};

export type FieldCheckIn = {
  field: string;
  operator: FieldOperator;
  value?: unknown;
};

export type MembershipRuleIn = {
  rule_id?: string;
  event_key: string;
  field_checks?: FieldCheckIn[];
  ttl_days?: number | null;
};

/** GET echo. `event_key` is null for legacy/uncatalogued stored trigger keys. */
export type MembershipRuleOut = Omit<MembershipRuleIn, "event_key"> & {
  event_key: string | null;
  trigger_key?: string;
};

// ─── Create request bodies ────────────────────────────────────────────────────

export type TemplateIn = {
  name: string;
  description?: string;
  default_steps: StepSpecIn[];
  /** @deprecated — prefer steps[].banner */
  default_localized_strings?: Record<string, Record<string, string>>;
  default_caps?: CapSpecIn;
  default_eligibility?: ConditionSpecIn[];
};

export type CampaignIn = {
  name: string;
  description?: string;
  starts_at: string;
  priority?: number;
  steps: StepSpecIn[];
  ends_at?: string | null;
  /** @deprecated — prefer steps[].banner */
  localized_strings?: Record<string, Record<string, string>>;
  caps?: CapSpecIn;
  eligibility_conditions?: ConditionSpecIn[];
  created_from_template_id?: string | null;
  // Phase 5 — per-affiliate campaign routing
  affiliate_referral_code?: string | null;
  // BONENG-722/724 — referral campaign category. `referral_config` is required
  // when `category === "referral"` and forbidden otherwise (validated upstream).
  category?: CampaignCategory;
  referral_config?: ReferralConfigIn;
};

export type SegmentIn = {
  name: string;
  description?: string;
  membership_rules?: MembershipRuleIn[];
  membership_ttl_days?: number | null;
};

/**
 * Create/update body for a series campaign (BONENG-842). Mirrors `CampaignIn`
 * minus fields upstream `SeriesIn` forbids (`extra="forbid"`): `ends_at`
 * (series never expire), `affiliate_referral_code`, `category` (use case
 * hardcodes SERIES), `referral_config`, and `localized_strings`. `steps` is
 * constrained by the backend to the fixed two-step blueprint (step 1
 * `schedule:campaign_start` + `show_bonus` OFFER; step 2 event trigger +
 * AWARD) — the FE builds that shape rather than allowing arbitrary steps.
 */
export type SeriesIn = Omit<
  CampaignIn,
  | "ends_at"
  | "affiliate_referral_code"
  | "category"
  | "referral_config"
  | "localized_strings"
>;

// ─── Response shapes ─────────────────────────────────────────────────────────

export type TemplateOut = TemplateIn & {
  template_id: string;
  created_at: string;
  updated_at: string;
};

export type CampaignOut = Omit<CampaignIn, "priority"> & {
  campaign_id: string;
  status: DynamicBonusCampaignStatus;
  priority?: number | string;
  segment_name?: string | null;
  trigger_keys?: string[];
  created_by?: string;
  created_at: string;
  updated_at: string;
  /** Present when this campaign is a stamped series run (BONENG-842). */
  series_campaign_id?: string;
  batch_id?: string;
  /** Lifetime distinct-user counters (BONENG-966). Always present on a current
   *  backend but 0 until bonus.campaign_metrics is enabled; optional so a
   *  cached pre-deploy response still type-checks. */
  reached_users?: number;
  used_users?: number;
  category?: CampaignCategory;
};

/** True when a campaign GET is a series-stamped run (read-only in the FE). */
export const isSeriesRunCampaign = (
  campaign: Pick<CampaignOut, "series_campaign_id">,
): boolean => Boolean(campaign.series_campaign_id);

/** Response shape for a series campaign (BONENG-842). Read-only echo fields
 * (`category`, `localized_strings`) may appear on GET but must not be sent
 * on create/update — see `SeriesIn`. */
export type SeriesOut = SeriesIn & {
  series_campaign_id: string;
  status: DynamicBonusCampaignStatus;
  category?: "series";
  localized_strings?: Record<string, Record<string, string>>;
  created_by?: string;
  created_at: string;
  updated_at: string;
};

/**
 * A single subscription-triggered run stamped from a series (BONENG-842).
 * Runs are backend-authored and read-only in the FE — this extends
 * `CampaignOut` (a run drill-down reuses the existing campaign detail
 * endpoints) with the series lineage fields.
 */
export type RunOut = CampaignOut & {
  series_campaign_id: string;
  batch_id: string;
};

export type SegmentOut = Omit<SegmentIn, "membership_rules"> & {
  segment_id: string;
  status: SegmentStatus;
  membership_rules?: MembershipRuleOut[];
  created_by?: string;
  created_at: string;
  updated_at: string;
};

// ─── Event catalog ────────────────────────────────────────────────────────────

export type EventFieldConditionHint = {
  type_key: string;
  operators: string[];
  params: Record<string, unknown>;
};

export type CatalogEventField = {
  field: string;
  label: string;
  type: "money" | "number" | "string" | "boolean" | "datetime";
  conditions: EventFieldConditionHint[];
  /** Exact on-wire values when the field is an enum; omit/empty = free text. */
  allowed_values?: string[];
};

export type BonusTriggerEvent = {
  event_key: string;
  display_name: string;
  description: string;
  filterable_fields: CatalogEventField[];
  /**
   * Condition type_keys that apply to this event.
   * Absent on older backends — PAM fails open (shows all non-internal).
   */
  applicable_condition_types?: string[];
};

export type EventCategoryGroup = {
  category: string;
  events: BonusTriggerEvent[];
};

// ─── Catalog ─────────────────────────────────────────────────────────────────

export type CatalogConditionItem = {
  type_key: string;
  description: string;
  /** Soft-retired / engine-only conditions — hidden from authoring pickers. */
  internal?: boolean;
  /** Event payload fields this condition reads; empty = not event-shape-dependent. */
  required_event_fields?: string[];
  /**
   * Event keys this condition applies to.
   * `null` = universally applicable (generic user-data).
   * omitted = older catalog — PAM fails open (treat as applicable).
   */
  applicable_event_keys?: string[] | null;
};

export type CatalogResultItem = {
  type_key: string;
  result_type: ResultType;
  description: string;
};

export type DynamicBonusCatalog = {
  conditions: CatalogConditionItem[];
  results: CatalogResultItem[];
  /** `currency_code_map` keys per free-spin provider (bgaming / alea / threeoaks). */
  free_spin_currencies?: Record<string, string[]>;
};

// ─── Segment members ─────────────────────────────────────────────────────────

export type MembershipSource = "eventbridge" | "manual" | "bulk_import" | string;

export type SegmentMember = {
  user_id: string;
  source: MembershipSource;
  joined_at: string;
  refreshed_at: string;
  expires_at: string | null;
};

export type SegmentMembersPage = {
  items: SegmentMember[];
  count: number;
  next_page_key: string | null;
};

// ─── Series runs ──────────────────────────────────────────────────────────────

/** Paginated page of series runs (BONENG-842), mirrors `SegmentMembersPage`. */
export type SeriesRunsPage = {
  items: RunOut[];
  count: number;
  next_page_key: string | null;
};

// ─── Braze series payload (BONENG-1006) ───────────────────────────────────────

/** Braze send mode — drives the `batch_id` Liquid template the backend embeds. */
export type BrazeSendType = "scheduled" | "canvas";

/**
 * Unwrapped `data` from GET .../series/{id}/braze-payload. PAM renders
 * `payload` as-is for copy/paste into Braze; it does not assemble the JSON.
 */
export type BrazeSeriesPayloadOut = {
  series_campaign_id: string;
  series_name: string;
  series_status: DynamicBonusCampaignStatus;
  expiry_hours: number;
  send_type: BrazeSendType;
  webhook_path: string;
  warnings: string[];
  payload: {
    campaign_type: "dynamic_bonus_series_subscription";
    engine: "dynamic_bonuses";
    user_id: string;
    data: {
      series_campaign_id: string;
      batch_id: string;
      expiry_hours: number;
    };
  };
};

// ─── List / envelope ─────────────────────────────────────────────────────────

export type BonusAdminEnvelope<T> = {
  data: T;
  meta?: unknown;
};
